/**
 * Copy for the agent execute-with-confirm prompt.
 *
 * This is the consent surface between an autonomous agent and query
 * execution, so the copy is part of the security boundary, not decoration:
 *
 * - **Agent-neutral.** Nothing here names an agent. The main process
 *   deliberately never supplies an agent identity (an agent could otherwise
 *   name *itself* inside a security prompt), and Claude Code and Codex are
 *   co-equal everywhere in the product.
 * - **The SQL is shown, and shortening is admitted.** Several lines are
 *   previewed, not one: a payload is never on line one, so a first-line-only
 *   preview hid exactly the text this prompt exists to show. Whenever anything
 *   is still hidden the copy says so explicitly. The preview is display text
 *   only — execution always uses the tab's full, original SQL, never this
 *   string.
 * - **The target is named.** Every query tab is synced into every AI session's
 *   workspace, so an agent in a dev terminal can request execution of a tab
 *   bound to production. The connection and database are part of the prompt
 *   because "approve this SQL" is meaningless without "…against what".
 * - **Agent text is sanitized and subordinate.** The tab name and the agent's
 *   `reason` are stripped of control, bidi and zero-width characters (which
 *   can visually reorder or hide text) and bounded. `reason` is rendered
 *   after the SQL and explicitly attributed, so it can never read as
 *   app-authored reassurance.
 * - **The standing grant is described honestly.** "Always this session" also
 *   covers queries the agent writes *later* — the copy has to say that,
 *   because the consent the user gives is much broader than the statement in
 *   front of them.
 */

/** Characters of SQL shown in the prompt before the preview is cut. */
export const MAX_SQL_PREVIEW_LENGTH = 300;

/** Lines of SQL shown in the prompt before the preview is cut. */
export const MAX_SQL_PREVIEW_LINES = 4;

/** Characters of the agent-supplied reason shown in the prompt. */
export const MAX_REASON_DISPLAY_LENGTH = 160;

/** Characters of the tab name shown in the prompt. */
export const MAX_TAB_NAME_DISPLAY_LENGTH = 60;

/** Characters of the connection / database name shown in the prompt. */
export const MAX_TARGET_DISPLAY_LENGTH = 60;

/** Where the query would run. Read from the tab's own `connectionInfo`. */
export type ExecutionPromptTarget = {
  /** Connection alias, falling back to the server host when there is no alias. */
  connectionName?: string;
  database?: string;
};

export type ExecutionPromptInput = ExecutionPromptTarget & {
  tabName: string;
  /** Full SQL of the tab. Only ever previewed here — never executed from here. */
  sql: string;
  /** Agent-supplied justification, already bounded by the main-process parser. */
  reason?: string;
};

export type ExecutionPrompt = {
  title: string;
  description: string;
  /** True when the preview shows less than the SQL that would actually run. */
  truncated: boolean;
};

/**
 * Drop characters that can lie about what the user is reading: C0/C1 control
 * codes (terminal escapes, NUL, CR), bidirectional overrides and isolates,
 * zero-width joiners/spaces, and the BOM.
 */
function stripDeceptiveCharacters(value: string): string {
  return value.replace(
    // eslint-disable-next-line no-control-regex
    /[\u0000-\u0008\u000b-\u001f\u007f-\u009f\u00ad\u200b-\u200f\u202a-\u202e\u2060-\u2064\u2066-\u2069\ufeff]/g,
    ''
  );
}

/**
 * Sanitize, collapse whitespace and bound a piece of agent-supplied text.
 * Exported for reuse anywhere else agent-supplied free text reaches the UI
 * (e.g. the AI lock `message` shown by `AgentActivityChip.tsx`, Task T17) —
 * same category of risk (control chars, embedded newlines, unbounded length)
 * as the SQL preview / reason text this module was built for.
 */
export function displayText(value: string, maxLength: number): string {
  const cleaned = stripDeceptiveCharacters(value).replace(/\s+/g, ' ').trim();
  return cleaned.length > maxLength ? `${cleaned.slice(0, maxLength).trimEnd()}…` : cleaned;
}

/**
 * The opening lines of the SQL, sanitized and bounded by both a line count and
 * a character count. `truncated` is true whenever anything at all is hidden.
 */
export function buildSqlPreview(sql: string): { text: string; truncated: boolean } {
  const lines = stripDeceptiveCharacters(sql)
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter((line) => line.length > 0);

  if (lines.length === 0) return { text: '', truncated: false };

  let truncated = lines.length > MAX_SQL_PREVIEW_LINES;
  let text = lines.slice(0, MAX_SQL_PREVIEW_LINES).join('\n');

  if (text.length > MAX_SQL_PREVIEW_LENGTH) {
    text = text.slice(0, MAX_SQL_PREVIEW_LENGTH).trimEnd();
    truncated = true;
  }

  return { text: truncated ? `${text}…` : text, truncated };
}

/** "Connection: X · Database: Y", with explicit placeholders when unknown. */
function buildTargetLine(target: ExecutionPromptTarget): string {
  const connection = target.connectionName
    ? displayText(target.connectionName, MAX_TARGET_DISPLAY_LENGTH)
    : '';
  const database = target.database
    ? displayText(target.database, MAX_TARGET_DISPLAY_LENGTH)
    : '';

  return `Connection: ${connection || '(unknown)'} · Database: ${database || '(none selected)'}`;
}

/** Build the title/description shown in the confirmation toast. */
export function buildExecutionPrompt(input: ExecutionPromptInput): ExecutionPrompt {
  const preview = buildSqlPreview(input.sql);
  const tabName = displayText(input.tabName, MAX_TAB_NAME_DISPLAY_LENGTH) || 'Untitled';
  const reason = input.reason ? displayText(input.reason, MAX_REASON_DISPLAY_LENGTH) : '';

  const lines = [
    `An AI agent in your terminal asked to run tab "${tabName}".`,
    buildTargetLine(input),
    preview.text || '(this tab is empty)',
  ];

  if (preview.truncated) {
    lines.push('The preview is shortened — the full query will run.');
  }
  if (reason) {
    lines.push(`Reason from the agent: ${reason}`);
  }
  lines.push(
    '"Always this session" also approves every later query the agent writes, until this terminal session ends.'
  );

  return { title: 'Run this query?', description: lines.join('\n'), truncated: preview.truncated };
}
